import axios, { AxiosError } from 'axios'
import {
    useQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import type { Hdo, HdoFormInput } from '../types'

export function useHdos(distributionElectricityId) {
    return useQuery<Hdo[], AxiosError>({
        queryKey: ['hdos-' + distributionElectricityId],
        queryFn: async () => {
            const { data } : { data : Array<Hdo> } = await axios.get(
                '/api/hdo_time_frame/list?distribution_electricity_id=' + distributionElectricityId,
            )
            return data
        },
    })
}

export function useHdo(hdoId) {
    return useQuery<Hdo, AxiosError>({
        queryKey: ['hdo-' + hdoId],
        queryFn: async () => {
            const { data } : { data : Hdo } = await axios.get(
                '/api/hdo_time_frame/single?id=' + hdoId,
            )
            return data
        },
    })
}

export const useCreateHdo = (distributionElectricityId, selectedDay, returnToList) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: HdoFormInput) => {            
            const beginsAt = data.begins_at.toISOString()
            const endsAt = data.ends_at.toISOString()
            return axios.get(`/api/hdo_time_frame/create?distribution_electricity_id=${distributionElectricityId
                }&begins_at=${beginsAt
                }&ends_at=${endsAt
                }&day_of_week=${selectedDay
                }&on=true`
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['hdos-' + distributionElectricityId] })
            returnToList()
        },
    })
}

export const useUpdateHdo = (hdoId, distributionElectricityId, resetDrawer) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: HdoFormInput) => {
            const beginsAt = data.begins_at.toISOString()
            const endsAt = data.ends_at.toISOString()
            return axios.get(`/api/hdo_time_frame/update?id=${hdoId
                }&begins_at=${beginsAt
                }&ends_at=${endsAt
                }&on=true`
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['hdos-' + distributionElectricityId] })
            queryClient.invalidateQueries({ queryKey: ['hdo-' + hdoId] })
            resetDrawer()
        },
    })
}

export const useDeleteHdo = (hdoId, distributionElectricityId) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => {
            return axios.get(`/api/hdo_time_frame/delete?id=${hdoId}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['hdos-' + distributionElectricityId] })
        },
    })
}

const createHdo = (originalHdo: Hdo, selectedDay: number) => {
    return axios.get(`/api/hdo_time_frame/create?distribution_electricity_id=${encodeURIComponent(originalHdo.distribution_electricity_id)
        }&begins_at=${encodeURIComponent(originalHdo.begins_at)
        }&ends_at=${encodeURIComponent(originalHdo.ends_at)
        }&day_of_week=${encodeURIComponent(selectedDay)
        }&on=true`
    )
}

export function useDuplicateHdoToOtherDays(currentDayHdos: Hdo[], selectedDay: number) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async function () {
            const tasks: Promise<any>[] = []

            currentDayHdos.forEach(item => {
                for(let day = 0; day < 7; day++) { // for each day of week
                    if(day !== selectedDay) { // except the day we are copying
                        tasks.push(createHdo(item, day))
                    }
                }
            })

            return Promise.all(tasks)
        },
        onSuccess: () => {
            // they all share this id
            const distributionElectricityId: number = currentDayHdos.length > 0 ? currentDayHdos[0].distribution_electricity_id : 0

            queryClient.invalidateQueries({ queryKey: [ 'hdos-' + distributionElectricityId ] })
        }
    })
}
